import Content, { ContentNS } from "content/content";
import {
  getNavigateToDeclarations,
  getPaxSessionRelativeUrl,
} from "features/shared/hooks/navigation/useFlowRedirect";
import useNavigateWithQuery from "features/shared/hooks/navigation/useNavigateWithQuery";
import usePaxSession from "features/shared/hooks/usePaxSession";
import ChecklistItem from "models/checklist/checklist-item/checklist-item";
import { FC } from "react";
import { useAppStore } from "store/store";

import VisaBannerClient from "./VisaBannerClient";
import useVisaBannerReason from "./useVisaBannerReason";
import { VisaRequirementReason } from "./visa-requirement-reason";

interface VisaBannerProps {
  item: ChecklistItem;
}

/*
Explains why the passenger was asked for a visa and offers a way back to the
answers that caused it - a passenger who doesn't actually need one would
otherwise just drop out of the flow here.

Rendered only for clients that ship the copy for it.
*/
const VisaBanner: FC<VisaBannerProps> = ({ item }) => {
  useAppStore((state) => state.locale);

  const sessionId = useAppStore((state) => state.sessionId);
  const paxSession = usePaxSession();
  const navigate = useNavigateWithQuery();
  const reason = useVisaBannerReason(item);

  const textDocs = (s: string, args: any = {}) =>
    Content.shared.t(s, { ns: ContentNS.docs, ...args });

  const textNationality = (s: string) =>
    Content.shared.t(s, { ns: ContentNS.nationalities });

  if (!paxSession || reason === null) {
    return null;
  }

  const questions = paxSession.checklist.questions;

  const onReviewAnswers = () => {
    const haveDocumentQuestions = questions.filter(
      (question) => question.type === "have-document-question"
    );

    // send the passenger to the step that holds the answer they need to change
    if (
      reason === VisaRequirementReason.TripLength ||
      haveDocumentQuestions.length === 0
    ) {
      const [url, state] = getNavigateToDeclarations(
        sessionId,
        paxSession.paxSessionId,
        true
      );
      navigate(url, { state });
      return;
    }

    navigate(
      `${getPaxSessionRelativeUrl(sessionId, paxSession.paxSessionId)}/questions`,
      {
        state: {
          showOnlyHaveDocumentQuestions: true,
          isDeclaration: false,
          isEditing: true,
        },
      }
    );
  };

  return (
    <VisaBannerClient
      title={`${textDocs("visa-banner.title")}`}
      description={`${textDocs(`visa-banner.reason.${reason}`, {
        destination: textNationality(item.ruleOrigin.country.toUpperCase()),
      })}`}
      footer={`${textDocs("visa-banner.footer")}`}
      buttonText={`${textDocs("visa-banner.cta")}`}
      isButtonVisible={questions.length > 0}
      onClick={onReviewAnswers}
    />
  );
};

export default VisaBanner;
